

import {memo, useEffect, useId, useRef, useState} from "react";
import {
    Button,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    InputGroup,
    Label,
    List,
    ListInlineItem,
    ModalBody,
    ModalFooter,
    Spinner,
} from "reactstrap";
import {PiEye, PiEyeSlash} from "react-icons/pi";
import {LuArrowUpRight} from "react-icons/lu";
import {Link, useSearchParams} from "react-router-dom";
import {
    AuthCustomModalDescriptionStyle, AuthCustomModalDescriptionStyleB
} from "../../../../layouts/auth/auth.layout.style";
import {Controller, useForm} from "react-hook-form";
import {activateNotification} from "../../../../utils/notifications-utils";
import {useAuth} from "../../../../providers/authentication.provider";
import {useNavigate} from "react-router";


export const SignInFormSection = memo(({toggle, onSuccess, setPendingCredentials, setStep}) => {
    const required = v => !!v?.trim();

    const {login, resendOtp, isLoading} = useAuth()
    const [passwordVisibility, setPasswordVisibility] = useState(false);

    const formId = useId()
    const {control, activateAccount, handleSubmit, setValue, formState: {errors}, reset} = useForm({
        mode: "onChange", defaultValues: {
            username: "", password: ""
        }
    });

    const [responseErrors, setResponseErrors] = useState([]);
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const onSubmit = async (formData) => {
        setPendingCredentials(formData)
        const {success, message, data} = await login(formData, () => {
            reset({
                username: "", password: ""
            });
        });
        activateNotification({title: success ? "Success" : "Error", body: message});
        if (success) {
            if (onSuccess) {
                onSuccess()
                toggle()
            } else {
                navigate(searchParams.get("redirect_to") || "/")
            }
            return
        }
        const msg = message.toLowerCase();
        if (msg.includes("inactive") || msg.includes("activate")) {
            await resendOtp(formData, () => {
                reset({
                    username: "", password: ""
                });
            });
            return setStep(2)
        }
        if (data?.errors) return setResponseErrors(data.errors);
    };

    return (<Form onSubmit={handleSubmit(onSubmit)}>
            <div className={"body"}>
                <FormGroup>
                    <Label for={`username-${formId}`}>Email / Phone Number</Label>
                    <Controller
                        name={"username"}
                        control={control}
                        rules={{
                            required: "Enter your email or phone number",
                            validate: v => v?.replace(/\s+/g, "").length > 3 || "Enter your email or phone number",
                            minLength: {
                                value: 2, message: "Min length is 2 characters",
                            },
                        }}
                        render={({field}) => (<Input
                            {...field}
                            id={`username-${formId}`}
                            type="text"
                            invalid={!!errors.username}
                            placeholder="Enter your email or phone number"
                        />)}
                    />
                    {errors.username?.message && (<FormFeedback>{errors.username?.message}</FormFeedback>)}
                </FormGroup>
                <FormGroup>
                    <Label for={`password-${formId}`}>Password</Label>
                    <InputGroup className={"has-icon"}>
                        <Controller
                            name={"password"}
                            control={control}
                            rules={{
                                required: "Password is required", validate: required,
                            }}
                            render={({field}) => (<Input
                                    {...field}
                                    type={passwordVisibility ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    name="password"
                                    id={`password-${formId}`}
                                    invalid={!!errors.password}
                                />

                            )}
                        />
                        <span className={"input-group-icon"}
                              onClick={() => setPasswordVisibility((prevState) => !prevState)}
                              style={{cursor: "pointer"}}>
                                        {passwordVisibility ? <PiEyeSlash color={"#CCCCCC"} size={20}/> :
                                            <PiEye color={"#CCCCCC"} size={20}/>}
                            </span>
                    </InputGroup>
                    {errors.password && (<p className={"invalid-error"}>{errors.password.message}</p>)}
                </FormGroup>
                {responseErrors.length > 0 && (<List type={"inline"}>
                    {responseErrors.map((error, index) => (<ListInlineItem className={"tw-text-red-500"}
                                                                           key={index}>{error}</ListInlineItem>))}
                </List>)}
                <AuthCustomModalDescriptionStyle>
                    I forgot my password? <Link to="/reset-password">Reset it</Link>
                </AuthCustomModalDescriptionStyle>
            </div>
            <ModalFooter>
                <AuthCustomModalDescriptionStyleB className="my-md-4 tw-text-center my-2">
                    By proceeding, you agree to our <a href="/terms-and-conditions">Terms of Use</a> and <a
                    href="/privacy-policy">Privacy Policy</a>
                </AuthCustomModalDescriptionStyleB>
                <Button type={"submit"}>
                    <span>Login</span>
                    {isLoading ? (<Spinner size="sm">Loading...</Spinner>) : (<LuArrowUpRight size={25}/>)}
                </Button>
                <AuthCustomModalDescriptionStyle>Don't have an account? <Link to="/create-account">Create
                    account</Link></AuthCustomModalDescriptionStyle>
            </ModalFooter>
        </Form>

    );
});
